// GoLiveDonationModal.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import nextarrow from "../assets/images/icons/next-arrow.svg";
import Dropdown from "../assets/images/icons/arrow-down.svg";

const EMPTY_FORM = {
  title: "",
  category: "",
  description: "",
  goalAmount: "",
  bankName: "",
  ifsc: "",
  beneficiaryName: "",
  accountNumber: "",
};

const REQUIRED_FIELDS = {
  title: "This field is required",
  category: "This field is required",
  goalAmount: "This field is required",
  bankName: "Enter Bank Name",
  ifsc: "Enter Code",
  beneficiaryName: "Enter Name",
  accountNumber: "Enter Account Number",
};

// LiveStreams.goal_amount is DECIMAL(10,2); the Organizers bank columns are VARCHAR(100)
const MAX_GOAL_AMOUNT = 99999999.99;
const MAX_BANK_FIELD_LENGTH = 100;
const SWIFT_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const BANK_NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9&.,'()\- ]{2,100}$/;
const PERSON_NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z.'\- ]{2,100}$/;
const ACCOUNT_NUMBER_PATTERN = /^[A-Za-z0-9]{6,34}$/;

// SWIFT codes are upper case and codes/account numbers have no spaces, also for details saved earlier
const normalizeBankCode = (value) => String(value ?? "").replace(/\s/g, "").toUpperCase();
const normalizeAccountNumber = (value) => String(value ?? "").replace(/\s/g, "");

const validateForm = (form) => {
  const errors = {};
  const code = normalizeBankCode(form.ifsc);
  Object.entries(REQUIRED_FIELDS).forEach(([field, message]) => {
    if (String(form[field] ?? "").trim() === "") errors[field] = message;
  });

  const goal = Number(form.goalAmount);
  if (!errors.title && form.title.trim().length < 3) errors.title = "Title must be at least 3 characters";
  if (!errors.goalAmount) {
    if (!(goal > 0)) errors.goalAmount = "Goal amount must be greater than 0";
    else if (goal > MAX_GOAL_AMOUNT) errors.goalAmount = `Goal amount can't be more than $${MAX_GOAL_AMOUNT.toLocaleString("en-US")}`;
    else if (!/^\d+(\.\d{1,2})?$/.test(String(form.goalAmount))) errors.goalAmount = "Use up to 2 decimal places";
  }
  if (!errors.bankName && !BANK_NAME_PATTERN.test(form.bankName.trim())) {
    errors.bankName = "Enter a valid bank name (letters, numbers and & . , ' ( ) - only)";
  }
  if (!errors.ifsc && !SWIFT_PATTERN.test(code)) {
    errors.ifsc = "Enter a valid SWIFT code (e.g. HDFCINBB)";
  }
  if (!errors.beneficiaryName && !PERSON_NAME_PATTERN.test(form.beneficiaryName.trim())) {
    errors.beneficiaryName = "Enter a valid name (letters, spaces and . ' - only)";
  }
  if (!errors.accountNumber && !ACCOUNT_NUMBER_PATTERN.test(normalizeAccountNumber(form.accountNumber))) {
    errors.accountNumber = "Account number must be 6–34 letters or digits";
  }
  return errors;
};

const GoLiveDonationModal = ({
  isOpen,
  onClose,
  logo,
  closeIcon,
  categories = [],
  initialData = null,
  onSave = null,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (isOpen && initialData) {
      setFormData({ ...EMPTY_FORM, ...initialData });
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === "ifsc") value = normalizeBankCode(value);
    if (name === "accountNumber") value = normalizeAccountNumber(value);
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    const newErrors = validateForm(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const data = {
      ...formData,
      title: formData.title.trim(),
      description: formData.description.trim(),
      bankName: formData.bankName.trim(),
      ifsc: normalizeBankCode(formData.ifsc),
      beneficiaryName: formData.beneficiaryName.trim(),
      accountNumber: normalizeAccountNumber(formData.accountNumber),
    };
    if (onSave) {
      onSave(data);
      onClose();
    } else {
      navigate("/golivedonation", { state: data });
    }
  };

  return (
    <div>
      <div className=" get-verified popup-share fixed inset-0 flex items-center justify-center bg-[#000] bg-opacity-60 w-full backdrop-blur-md">
        <div className="border-popup bg-white rounded-lg  relative">
          <div className="flex justify-between">
            <div className="logo-popup flex items-center justify-center space-x-2 ">
              <img src={logo} alt="Nyawoo Logo" className="w-50" />
            </div>
            {/* Close Button */}
            <button onClick={onClose}>
              <img src={closeIcon} alt="close" className="w-8 h-8" />
            </button>
          </div>
          <div className="border-t-[#D9D9D9] border-t-2">
            <form onSubmit={handleNext} className="p-8 space-y-10">
              {/* Fund Information Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[#095228]">
                  Fund Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      maxLength={255}
                      placeholder="Enter title"
                      className="w-full h-12 px-4 rounded-xl border border-[#095228] focus:outline-none"
                    />
                    {errors.title && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.title}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Category
                    </label>
                    <div className="relative">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`w-full h-12 px-4 rounded-xl border border-[#095228] appearance-none ${
                          formData.category === ""
                            ? "text-gray-400"
                            : "text-black"
                        }`}
                      >
                        <option value="" disabled hidden>
                          Select Category
                        </option>
                        {categories.map((cat) => {
                          const name = cat.title || cat.name || cat.category_name;
                          return (
                            <option key={cat.id || cat._id || name} value={name}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                        <img src={Dropdown} alt="" className="w-4 h-4" />
                      </div>
                    </div>
                    {errors.category && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.category}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={2000}
                    placeholder="Enter Description"
                    className="w-full px-4 py-3 rounded-xl border border-[#095228] resize-none focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Goal Amount
                    </label>
                    <div className="flex items-center border border-[#095228] rounded-xl h-12 px-3">
                      <span className="mr-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="goalAmount"
                        min="0.01"
                        max={MAX_GOAL_AMOUNT}
                        step="0.01"
                        value={formData.goalAmount}
                        onChange={handleChange}
                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                        placeholder="0"
                        className="w-full focus:outline-none"
                      />
                    </div>
                    {errors.goalAmount && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.goalAmount}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Bank Details Section */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[#095228]">
                  Bank Account Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      maxLength={MAX_BANK_FIELD_LENGTH}
                      placeholder="Enter Your Bank Name"
                      className="w-full h-12 px-4 rounded-xl border border-[#095228] focus:outline-none"
                    />
                    {errors.bankName && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.bankName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      SWIFT Code
                    </label>
                    <input
                      type="text"
                      name="ifsc"
                      value={formData.ifsc}
                      onChange={handleChange}
                      maxLength={11}
                      placeholder="Enter Your SWIFT Code"
                      className="w-full h-12 px-4 rounded-xl border border-[#095228] focus:outline-none"
                    />
                    {errors.ifsc && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.ifsc}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Beneficiary Name
                    </label>
                    <input
                      type="text"
                      name="beneficiaryName"
                      value={formData.beneficiaryName}
                      onChange={handleChange}
                      maxLength={MAX_BANK_FIELD_LENGTH}
                      placeholder="Enter Your Beneficiary Name"
                      className="w-full h-12 px-4 rounded-xl border border-[#095228] focus:outline-none"
                    />
                    {errors.beneficiaryName && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.beneficiaryName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      maxLength={34}
                      placeholder="Enter Your Account Number"
                      className="w-full h-12 px-4 rounded-xl border border-[#095228] focus:outline-none"
                    />
                    {errors.accountNumber && (
                      <p className="text-red-500 text-xs mt-1 ml-1">
                        {errors.accountNumber}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="flex items-center gap-2 bg-[#095228] text-white px-8 h-12 rounded-full hover:bg-green-900 transition"
                    >
                      {onSave ? "Save" : "Next"}
                      <img src={nextarrow} className="ml-5" alt="next" />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoLiveDonationModal;
